import { createSlice } from "@reduxjs/toolkit";
import { sanitizeProfileForStorage } from "../../services/userDataService";

const storedUser = localStorage.getItem("user");
let initialUser = null;
let isInitialSessionValid = false;

// Safely parse stored user data
if (storedUser) {
  try {
    initialUser = JSON.parse(storedUser);
    isInitialSessionValid = !!initialUser?.userData;
    if (initialUser?.profileData) {
      initialUser.profileData = sanitizeProfileForStorage(initialUser.profileData);
    }
  } catch (error) {
    localStorage.removeItem("user");
  }
}

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: isInitialSessionValid ? initialUser : null,
    isAuthenticated: !!isInitialSessionValid,
    tokenExpiry: null,
    keepMeLogin: isInitialSessionValid && initialUser?.keepMeLogin ? initialUser.keepMeLogin : false,
    profileData: isInitialSessionValid && initialUser?.profileData ? initialUser.profileData : null,
    settingsData: isInitialSessionValid && initialUser?.settingsData ? initialUser.settingsData : null,
    lastDataRefresh: isInitialSessionValid && initialUser?.lastDataRefresh ? initialUser.lastDataRefresh : null,
  },
  reducers: {
    login: (state, action) => {
      const payload = {
        ...action.payload,
        profileData: sanitizeProfileForStorage(action.payload.profileData) || null,
      };
      state.user = payload;
      state.isAuthenticated = true;
      state.keepMeLogin = payload.keepMeLogin || false;
      state.profileData = payload.profileData || null;
      state.settingsData = payload.settingsData || null;
      state.lastDataRefresh = payload.lastDataRefresh || null;
      state.tokenExpiry = null;
      localStorage.setItem("user", JSON.stringify(payload));
    },
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.tokenExpiry = null;
      state.keepMeLogin = false;
      state.profileData = null;
      state.settingsData = null;
      state.lastDataRefresh = null;
      localStorage.removeItem("user");
      localStorage.removeItem("keepMeLogin");
      localStorage.removeItem("itemGridPreferences");
    },
    updateToken: (state, action) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        if (action.payload.keepMeLogin !== undefined) {
          state.keepMeLogin = action.payload.keepMeLogin;
        }
        localStorage.setItem("user", JSON.stringify(state.user));
      }
    },
    updateProfileData: (state, action) => {
      state.profileData = sanitizeProfileForStorage(action.payload);
      if (state.user) {
        state.user.profileData = state.profileData;
        state.user.lastDataRefresh = new Date().toISOString();
        state.lastDataRefresh = new Date().toISOString();
        localStorage.setItem("user", JSON.stringify(state.user));
      }
    },
    updateSettingsData: (state, action) => {
      state.settingsData = action.payload;
      if (state.user) {
        state.user.settingsData = action.payload;
        state.user.lastDataRefresh = new Date().toISOString();
        state.lastDataRefresh = new Date().toISOString();
        localStorage.setItem("user", JSON.stringify(state.user));
      }
    },
    updateUserData: (state, action) => {
      state.profileData =
        sanitizeProfileForStorage(action.payload.profileData) || state.profileData;
      state.settingsData = action.payload.settingsData || state.settingsData;
      state.lastDataRefresh = new Date().toISOString();
      if (state.user) {
        state.user.profileData = state.profileData;
        state.user.settingsData = state.settingsData;
        state.user.lastDataRefresh = state.lastDataRefresh;
        localStorage.setItem("user", JSON.stringify(state.user));
      }
    },
    /** Merge fields into the logged-in staff userData (e.g. refreshed FirstName). */
    patchUserData: (state, action) => {
      if (!state.user) return;
      const patch = action.payload || {};
      state.user.userData = {
        ...(state.user.userData || {}),
        ...patch,
      };
      localStorage.setItem("user", JSON.stringify(state.user));
    },
    checkTokenExpiry: (state) => {
      // Cookie-based session validity is checked server-side.
      state.tokenExpiry = null;
    },
  },
});

export const {
  login,
  logout,
  updateToken,
  updateProfileData,
  updateSettingsData,
  updateUserData,
  patchUserData,
  checkTokenExpiry,
} = authSlice.actions;
export default authSlice.reducer;
